import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoIssuesDashboardDeferredResult,
  RepoIssuesDashboardContentResponseSchema,
  RepoIssuePullRequestReviewDecision,
  RepoIssuePullRequestState,
  RepoIssuePullRequestStatusCheckState,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import type {IssuesListSecondaryResult} from '../components/IssuesListSurface'
import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {createRowNodeLoader} from './issues-dashboard-row-node'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'

export type RepoRowItem = ReturnType<typeof repoIssuesToListItems>[number]

type RowsEnvelope = {
  payload?: {
    issuesIndexRowsData?: unknown
  }
}

export function shouldFetchReactions(query: string): boolean {
  return /\bsort:reactions(?:-\S+)?\b/.test(query)
}

function pullRequestState(state: RepoIssuePullRequestState): string | null {
  switch (state) {
    case RepoIssuePullRequestState.OPEN:
      return 'OPEN'
    case RepoIssuePullRequestState.CLOSED:
      return 'CLOSED'
    case RepoIssuePullRequestState.MERGED:
      return 'MERGED'
    case RepoIssuePullRequestState.UNSPECIFIED:
      return null
  }
}

function reviewDecision(state: RepoIssuePullRequestReviewDecision) {
  switch (state) {
    case RepoIssuePullRequestReviewDecision.APPROVED:
      return 'APPROVED' as const
    case RepoIssuePullRequestReviewDecision.CHANGES_REQUESTED:
      return 'CHANGES_REQUESTED' as const
    case RepoIssuePullRequestReviewDecision.REQUIRED:
      return 'REVIEW_REQUIRED' as const
    case RepoIssuePullRequestReviewDecision.UNSPECIFIED:
      return undefined
  }
}

function statusCheckState(state: RepoIssuePullRequestStatusCheckState) {
  switch (state) {
    case RepoIssuePullRequestStatusCheckState.SUCCESS:
      return 'SUCCESS' as const
    case RepoIssuePullRequestStatusCheckState.FAILURE:
      return 'FAILURE' as const
    case RepoIssuePullRequestStatusCheckState.PENDING:
      return 'PENDING' as const
    case RepoIssuePullRequestStatusCheckState.UNSPECIFIED:
      return undefined
  }
}

export function repoDeferredToListItem(
  result: RepoIssuesDashboardDeferredResult,
): IssuesListSecondaryResult | undefined {
  if (result.item.case === 'issue') {
    const issue = result.item.value
    return {
      itemType: 'issue',
      id: result.id,
      isReadByCurrentUser: issue.isReadByCurrentUser,
      linkedPullRequestCount: issue.linkedPullRequestCount,
      linkedPullRequests: issue.linkedPullRequests.map(pullRequest => ({
        title: pullRequest.title,
        number: pullRequest.number,
        permalink: pullRequest.permalink ?? null,
        repoNameWithOwner: pullRequest.repoNameWithOwner ?? null,
        state: pullRequestState(pullRequest.state),
        isDraft: pullRequest.isDraft,
      })),
      subIssuesSummary: issue.subIssuesSummary ?? {total: 0, completed: 0, percentCompleted: 0},
      blockedByCount: issue.blockedByCount,
      reactionGroups: issue.reactionGroups?.groups ?? null,
    }
  }

  if (result.item.case === 'pullRequest') {
    const pullRequest = result.item.value
    const state = pullRequest.statusCheckRollup && statusCheckState(pullRequest.statusCheckRollup.state)
    return {
      itemType: 'pull_request',
      id: result.id,
      reviewDecisionState: reviewDecision(pullRequest.reviewDecisionState),
      statusCheckRollup:
        pullRequest.statusCheckRollup && state
          ? {
              state,
              totalCount: pullRequest.statusCheckRollup.totalCount,
              successCount: pullRequest.statusCheckRollup.successCount,
            }
          : undefined,
      approvedReviewsCount: pullRequest.approvedReviewsCount,
      changesRequestedReviewsCount: pullRequest.changesRequestedReviewsCount,
      reactionGroups: pullRequest.reactionGroups?.groups,
    }
  }
}

/**
 * Fetch fresh enriched rows for the repository Issues index from
 * `GET /:owner/:repo/issues/_rows`, keyed by each row's global identity so the
 * batching loader can resolve individual requests.
 */
export async function fetchRepoRowNodes(
  owner: string,
  repo: string,
  globalIds: string[],
  fetchReactions = false,
): Promise<Map<string, RepoRowItem>> {
  const {json} = await queryFnFetchWithProtoSignal<RowsEnvelope>({
    routeId: 'issuesIndexRowsData',
    queryName: 'rows',
    queryDeps: {
      pathname: `/${owner}/${repo}/issues/_rows`,
      searchParams: {ids: globalIds.join(','), fetch_reactions: fetchReactions.toString()},
    },
  })
  const rawRows = json.payload?.issuesIndexRowsData
  if (!rawRows) return new Map()

  const rows = fromJson({
    schema: RepoIssuesDashboardContentResponseSchema,
    raw: rawRows,
    routeId: 'issuesIndexRowsData',
    queryName: 'rows',
  })
  const deferred = rows.deferred.map(repoDeferredToListItem).filter(item => item !== undefined)
  const results = mergeDeferredIssues(repoIssuesToListItems(rows.results), deferred)

  const byIdentity = new Map<string, RepoRowItem>()
  for (const item of results) {
    byIdentity.set(getListItemIdentity(item), item)
  }
  return byIdentity
}

// The repository Issues index row-node loader, over GET /:owner/:repo/issues/_rows.
// Built per page (owner/repo are stable there) so it reuses the shared batching
// queue without sharing pending state with the dashboard loader.
export function createRepoRowNodeLoader(
  owner: string,
  repo: string,
  fetchReactions = false,
): {
  loadRowNode: (globalId: string) => Promise<RepoRowItem | undefined>
} {
  return createRowNodeLoader<RepoRowItem>(globalIds => fetchRepoRowNodes(owner, repo, globalIds, fetchReactions))
}
